import { useLoaderData } from "@tanstack/react-router";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Search,
  X,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { PageHeader } from "@/components/shared/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface Deal {
  id: string;
  hubspotDealId: string;
  dealname: string | null;
  amount: number | null;
  dealstage: string | null;
  pipeline: string | null;
  closedate: string | null;
  hubspotOwnerId: string | null;
  hsLastModifiedDate: string | null;
  hubspotCreatedate: string | null;
  freeQuoteFormFilled: string | null;
  orderDate: string | null;
  typeOfError: string | null;
  accuracyError: string | null;
  amountInHomeCurrency: number | null;
  netPrice: number | null;
  errorNotes: string | null;
  source: string | null;
  college: string | null;
  productType: string | null;
  ownerName: string | null;

  archivedAt: string | null;
  syncedAt: string | number;
  createdAt: string | number;
  updatedAt: string | number;
}

interface Pipeline {
  id: string;
  label: string;
  stages: { id: string; label: string }[];
}

function formatCurrency(amount: number | null): string {
  if (amount === null || amount === undefined) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatDate(date: string | null): string {
  if (!date) return "—";
  try {
    const [year, month, day] = date.split("-").map(Number);
    if (!year || !month || !day) return "—";
    return new Date(year, month - 1, day).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "—";
  }
}

function getStageName(stageId: string | null, pipelines: Pipeline[]): string {
  if (!stageId) return "—";
  for (const p of pipelines) {
    const stage = p.stages.find((s) => s.id === stageId);
    if (stage) return stage.label;
  }
  return stageId;
}

function getPipelineName(pipelineId: string | null, pipelines: Pipeline[]): string {
  if (!pipelineId) return "—";
  return pipelines.find((p) => p.id === pipelineId)?.label ?? pipelineId;
}

export function DealsPage() {
  const {
    deals: initialDeals,
    total: initialTotal,
    pipelines,
  } = useLoaderData({
    from: "/_authenticated/deals",
  }) as {
    deals: Deal[];
    total: number;
    pipelines: Pipeline[];
  };

  const [deals, setDeals] = useState(initialDeals);
  const [total, setTotal] = useState(initialTotal);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [pipelineFilter, setPipelineFilter] = useState("");
  const [sortBy, setSortBy] = useState("hubspotCreatedate");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [loading, setLoading] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState<Deal | null>(null);

  const pageSize = 50;
  const totalPages = Math.ceil(total / pageSize);

  const fetchDeals = useCallback(
    async (
      p: number,
      s: string,
      pipeline: string,
      sort: string,
      dir: string,
      signal?: AbortSignal,
    ) => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          page: String(p),
          pageSize: String(pageSize),
          sortBy: sort,
          sortDir: dir,
        });
        if (s) params.set("search", s);
        if (pipeline) params.set("pipeline", pipeline);

        const res = await fetch(`/api/deals?${params}`, { signal });
        if (!res.ok) throw new Error("Failed to fetch");
        const data = await res.json();
        setDeals(data.deals ?? []);
        setTotal(data.total ?? 0);
      } catch (e) {
        if (e instanceof DOMException && e.name === "AbortError") return;
        console.error("Failed to fetch deals:", e);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  // Debounced search
  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(() => {
      setPage(1);
      fetchDeals(1, search, pipelineFilter, sortBy, sortDir, controller.signal);
    }, 300);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [search, pipelineFilter, sortBy, sortDir, fetchDeals]);

  function handlePageChange(newPage: number) {
    setPage(newPage);
    fetchDeals(newPage, search, pipelineFilter, sortBy, sortDir);
  }

  function handleSort(column: string) {
    if (sortBy === column) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(column);
      setSortDir("desc");
    }
  }

  function SortIcon({ column }: { column: string }) {
    if (sortBy !== column) return <ArrowUpDown className="ml-1 h-3 w-3 opacity-30" />;
    return sortDir === "asc" ? (
      <ArrowUp className="ml-1 h-3 w-3" />
    ) : (
      <ArrowDown className="ml-1 h-3 w-3" />
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Deals"
        description={`${total.toLocaleString()} deals mirrored from HubSpot`}
      />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search deals..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <Select value={pipelineFilter} onValueChange={setPipelineFilter}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="All Pipelines" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value=" ">All Pipelines</SelectItem>
            {pipelines.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {pipelineFilter && (
          <Button variant="ghost" size="sm" onClick={() => setPipelineFilter("")}>
            <X className="mr-1 h-3 w-3" />
            Clear filter
          </Button>
        )}
      </div>

      {/* Table */}
      <div className={`rounded-md border ${loading ? "opacity-60" : ""}`}>
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/30">
              <TableHead>
                <button
                  type="button"
                  onClick={() => handleSort("dealname")}
                  className="flex items-center text-xs uppercase tracking-wider hover:text-foreground"
                >
                  Deal Name <SortIcon column="dealname" />
                </button>
              </TableHead>
              <TableHead>
                <button
                  type="button"
                  onClick={() => handleSort("amount")}
                  className="flex items-center text-xs uppercase tracking-wider hover:text-foreground"
                >
                  Amount <SortIcon column="amount" />
                </button>
              </TableHead>
              <TableHead className="text-xs uppercase tracking-wider">Pipeline</TableHead>
              <TableHead className="text-xs uppercase tracking-wider">Stage</TableHead>
              <TableHead>
                <button
                  type="button"
                  onClick={() => handleSort("closedate")}
                  className="flex items-center text-xs uppercase tracking-wider hover:text-foreground"
                >
                  Close Date <SortIcon column="closedate" />
                </button>
              </TableHead>
              <TableHead>
                <button
                  type="button"
                  onClick={() => handleSort("hubspotCreatedate")}
                  className="flex items-center text-xs uppercase tracking-wider hover:text-foreground"
                >
                  Created <SortIcon column="hubspotCreatedate" />
                </button>
              </TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-right">HubSpot</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {deals.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="h-24 text-center text-muted-foreground">
                  {search || pipelineFilter
                    ? "No deals match your filters."
                    : "No deals synced yet."}
                </TableCell>
              </TableRow>
            ) : (
              deals.map((d) => (
                <TableRow
                  key={d.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => setSelectedDeal(d)}
                >
                  <TableCell className="max-w-[250px]">
                    <span className="font-medium truncate block">{d.dealname ?? "Untitled"}</span>
                  </TableCell>
                  <TableCell className="tabular-nums">{formatCurrency(d.amount)}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs">
                      {getPipelineName(d.pipeline, pipelines)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm">{getStageName(d.dealstage, pipelines)}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDate(d.closedate)}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDate(d.hubspotCreatedate)}
                  </TableCell>
                  <TableCell className="text-right">
                    <span className="text-xs font-mono text-muted-foreground">
                      {d.hubspotDealId}
                    </span>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of{" "}
            {total.toLocaleString()}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(page - 1)}
              disabled={page <= 1 || loading}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(page + 1)}
              disabled={page >= totalPages || loading}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Deal detail sheet */}
      <DealDetailSheet
        deal={selectedDeal}
        pipelines={pipelines}
        onClose={() => setSelectedDeal(null)}
      />
    </div>
  );
}

function DealDetailSheet({
  deal,
  pipelines,
  onClose,
}: {
  deal: Deal | null;
  pipelines: Pipeline[];
  onClose: () => void;
}) {
  return (
    <Sheet open={!!deal} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="overflow-y-auto sm:max-w-lg">
        {deal && (
          <>
            <SheetHeader>
              <SheetTitle className="text-lg leading-snug">
                {deal.dealname ?? "Untitled Deal"}
              </SheetTitle>
              <div className="flex items-center gap-2 pt-1">
                <Badge variant="outline" className="text-xs">
                  {getPipelineName(deal.pipeline, pipelines)}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  {getStageName(deal.dealstage, pipelines)}
                </span>
              </div>
            </SheetHeader>

            <div className="mt-6 space-y-6">
              {/* Core fields */}
              <div>
                <h4 className="mb-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Deal Info
                </h4>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  <dt className="text-muted-foreground">Owner</dt>
                  <dd className="font-medium">{deal.ownerName ?? "—"}</dd>
                  <dt className="text-muted-foreground">Amount</dt>
                  <dd className="font-medium tabular-nums">{formatCurrency(deal.amount)}</dd>
                  <dt className="text-muted-foreground">Amount (Home)</dt>
                  <dd className="font-medium tabular-nums">
                    {formatCurrency(deal.amountInHomeCurrency)}
                  </dd>
                  <dt className="text-muted-foreground">Net Price</dt>
                  <dd className="font-medium tabular-nums">{formatCurrency(deal.netPrice)}</dd>
                  <dt className="text-muted-foreground">Close Date</dt>
                  <dd>{formatDate(deal.closedate)}</dd>
                  <dt className="text-muted-foreground">Order Date</dt>
                  <dd>{formatDate(deal.orderDate)}</dd>
                  <dt className="text-muted-foreground">Quote Form Filled</dt>
                  <dd>{formatDate(deal.freeQuoteFormFilled)}</dd>
                  <dt className="text-muted-foreground">Error Type</dt>
                  <dd>{deal.typeOfError ?? "—"}</dd>
                  <dt className="text-muted-foreground">Accuracy Error</dt>
                  <dd>{deal.accuracyError ?? "—"}</dd>
                  <dt className="text-muted-foreground">Error Notes</dt>
                  <dd>{deal.errorNotes ?? "—"}</dd>
                  <dt className="text-muted-foreground">Source</dt>
                  <dd>{deal.source ?? "—"}</dd>
                  <dt className="text-muted-foreground">College</dt>
                  <dd>{deal.college ?? "—"}</dd>
                  <dt className="text-muted-foreground">Product Type</dt>
                  <dd>{deal.productType ?? "—"}</dd>
                  <dt className="text-muted-foreground">Created</dt>
                  <dd>{formatDate(deal.hubspotCreatedate)}</dd>
                  <dt className="text-muted-foreground">Last Modified</dt>
                  <dd>{formatDate(deal.hsLastModifiedDate)}</dd>
                  <dt className="text-muted-foreground">HubSpot ID</dt>
                  <dd>
                    <a
                      href={`https://app.hubspot.com/contacts/${deal.hubspotDealId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 font-mono text-xs text-primary hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {deal.hubspotDealId}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </dd>
                </dl>
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
